/**
 * 路由守卫配置
 * 处理认证、权限等路由拦截逻辑
 */

import type { Router, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/store';
import { enableLog, formatPrefix } from '@/utils/app-runtime';

const MODULE_PREFIX = formatPrefix('AuthGuard');

/**
 * 设置路由守卫
 */
export function setupRouterGuards(router: Router): void {
  // 全局前置守卫
  router.beforeEach(async (to, from, next) => {
    try {
      // 认证检查
      await checkAuthentication(to, from, next);
    } catch (error) {
      console.error('路由导航出错:', error);
      ElMessage.error('页面加载失败，请刷新重试');
      next(false);
    }
  });

  // 全局后置钩子
  router.afterEach((to, from) => {
    // 设置页面标题
    setPageTitle(to);
    
    // 记录页面访问
    recordPageVisit(to, from);
  });

  // 路由错误处理
  router.onError((error) => {
    // 检查是否是DOM相关错误，静默处理
    if (error.message && error.message.includes('parentNode')) {
      return;
    }

    // 检查是否是动态导入失败
    if (error.message && error.message.includes('Failed to fetch dynamically imported module')) {
      if (enableLog) {
        console.error(`${MODULE_PREFIX} 动态导入模块失败`, {
          error,
          context: { operation: 'routerDynamicImport' }
        });
      }
      ElMessage.error('页面模块加载失败，请清除缓存后重试');

      // 尝试重新加载页面
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      return;
    }

    // 其他错误显示提示
    if (enableLog) {
      console.error(`${MODULE_PREFIX} 路由错误`, {
        error,
        context: { operation: 'routerError' }
      });
    }
    ElMessage.error('页面加载失败，请刷新重试');
  });
}

/**
 * 认证检查
 */
async function checkAuthentication(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const userStore = useUserStore();



  // 等待用户状态初始化完成（优化后的逻辑）
  if (!userStore.isInitialized || userStore.isLoading) {
    // 等待用户状态初始化完成，但限制等待时间
    let attempts = 0;
    const maxAttempts = 30; // 最多等待3秒，避免长时间阻塞

    while ((!userStore.isInitialized || userStore.isLoading) && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
  }

  // 检查是否需要登录
  if (to.meta.requiresAuth && !userStore.isLoggedIn) {
    ElMessage.warning('请先登录');
    next('/');
    return;
  }

  // 如果是登录/注册页面且用户已登录,重定向到首页
  if ((to.path === '/auth/login' || to.path === '/auth/register') && userStore.isLoggedIn) {
    next('/');
    return;
  }

  // 权限检查
  if (!checkPermission(to, userStore)) {
    ElMessage.error('您没有访问该页面的权限');
    next('/');
    return;
  }

  next();
}

/**
 * 权限检查
 */
function checkPermission(route: RouteLocationNormalized, userStore: any): boolean {
  // 如果路由不需要特殊权限，直接通过
  if (!route.meta.requiredRole) {
    return true;
  }
  
  // 检查用户角色
  const userRole = userStore.currentUser?.role;
  const requiredRole = route.meta.requiredRole as string;

  // 简单的角色权限检查
  const roleHierarchy = ['user', 'vip', 'admin'];
  const userRoleIndex = roleHierarchy.indexOf(userRole || '');
  const requiredRoleIndex = roleHierarchy.indexOf(requiredRole || '');
  
  return userRoleIndex >= requiredRoleIndex;
}

/**
 * 设置页面标题
 */
function setPageTitle(route: RouteLocationNormalized): void {
  const title = route.meta.title as string;
  if (title) {
    document.title = `${title} - 小影配音`;
  } else {
    document.title = '小影配音';
  }
}

/**
 * 记录页面访问
 */
function recordPageVisit(_to: RouteLocationNormalized, _from: RouteLocationNormalized): void {
  // 记录页面访问统计
  if (import.meta.env.PROD) {
    // 生产环境可以添加统计代码
    // 这里可以集成第三方统计服务
  }
}

/**
 * 检查路由是否需要刷新
 */
export function shouldRefreshRoute(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
  // 如果是相同路由但参数不同，需要刷新
  return to.name === from.name && JSON.stringify(to.params) !== JSON.stringify(from.params);
}

/**
 * 获取面包屑导航
 */
export function getBreadcrumbs(route: RouteLocationNormalized): Array<{ name: string; path: string }> {
  const breadcrumbs: Array<{ name: string; path: string }> = [];
  
  // 添加首页
  breadcrumbs.push({ name: '首页', path: '/' });
  
  // 添加当前路由的面包屑
  if (route.matched.length > 0) {
    route.matched.forEach((match, index) => {
      if (match.meta.title && index > 0) {
        breadcrumbs.push({
          name: match.meta.title as string,
          path: match.path
        });
      }
    });
  }
  
  return breadcrumbs;
}
